import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../Context/AuthProvider";
import { fetchSecMasterItems } from "../../http/api";
import { useSecMasterOptions } from "./useSecMasterOptions";
import { useSecMasterOptionsStore } from "../../Store/secMasterOptionsStore";
import { buildWatchlistFilterPayload } from "../../Utils/maWatchlistFilterUtils";
import { WATCHLIST_FILTER_FIELDS } from "../MAInventoryComponents/watchlistFilterOptions";
import { BOND_TYPES } from "../../Constants/AppConstants";

// Tabs mirror the inventory search screen (US Treasury / Corporates / Municipals)
// while each tab drives the watchlist bond-type field set.
export const SEC_MASTER_SEARCH_TABS = [
  { label: "US Treasury", bondType: BOND_TYPES.TREASURY },
  { label: "Corporates", bondType: BOND_TYPES.CORPORATES },
  { label: "Municipals", bondType: BOND_TYPES.MUNICIPALS },
];

// SecMaster-specific field lists: identifier prepended to the shared watchlist
// fields. Kept here so MA Inventory is not affected.
export const SEC_MASTER_FILTER_FIELDS = {
  [BOND_TYPES.TREASURY]: ["identifier", ...(WATCHLIST_FILTER_FIELDS[BOND_TYPES.TREASURY] || [])],
  [BOND_TYPES.CORPORATES]: ["identifier", ...(WATCHLIST_FILTER_FIELDS[BOND_TYPES.CORPORATES] || [])],
  [BOND_TYPES.MUNICIPALS]: ["identifier", ...(WATCHLIST_FILTER_FIELDS[BOND_TYPES.MUNICIPALS] || [])],
};

// Wraps the shared payload builder and appends searchString when the user
// has entered an identifier — SecMaster-only key, not sent by MA Inventory.
export const buildSecMasterFilterPayload = (filterData) => {
  const base = buildWatchlistFilterPayload(filterData);
  const id = (filterData.identifier || "").trim();
  if (id) base.searchString = id;
  return base;
};

const BOND_TYPE_LIST = SEC_MASTER_SEARCH_TABS.map((tab) => tab.bondType);

// Empty draft filter shape — keys match the watchlist filter field names so the
// shared renderFilterField + buildWatchlistFilterPayload work unchanged.
const DEFAULT_FILTER_DATA = {
  identifier: "",
  securityCharge: "",
  side: "",
  couponMin: "",
  couponMax: "",
  maturityMin: "",
  maturityMax: "",
  priceMin: "",
  priceMax: "",
  yieldMin: "",
  yieldMax: "",
  duration: "",
  durationComparison: "",
  minimumRating: "",
  minimumRatingMoody: "",
  zeroCoupon: "",
  perpetual: "",
  callable: "",
  state: "",
  insured: "",
  taxable: "",
  paymentFrequency: "",
  incomeSource: "",
  altMinTax: "",
  sector: "",
  callStartDate: "",
  nextCallDate: "",
  issuer: "",
  conventionMethod: "",
  couponType: "",
  currency: "",
  issueCurrency: "",
  subType: "",
};

const COUNT_DEBOUNCE_MS = 400;

const getTabIndexForBondType = (bondType) => {
  const idx = SEC_MASTER_SEARCH_TABS.findIndex((t) => t.bondType === bondType);
  return idx >= 0 ? idx : 0;
};

// Extract the raw (watchlist-shaped) filter draft carried in navigation state.
const deriveAppliedFilterData = (navState) => {
  const incoming = navState?.secMasterFilterData;
  if (!incoming || typeof incoming !== "object") return null;
  return { ...incoming, bondType: navState?.bondType || incoming.bondType };
};

const makeEmptyDraftsByType = () =>
  BOND_TYPE_LIST.reduce((acc, bondType) => {
    acc[bondType] = { ...DEFAULT_FILTER_DATA };
    return acc;
  }, {});

export const useSecMasterSearch = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { options, error: optionsError } = useSecMasterOptions();

  // Default coupon/maturity ranges come from the shared store (preloaded by the
  // SecMaster screen, so they're usually already cached here).
  const defaultsByType = useSecMasterOptionsStore((s) => s.defaultsByType);
  const loadDefaults = useSecMasterOptionsStore((s) => s.loadDefaults);

  const initialApplied = deriveAppliedFilterData(location.state);

  // Per-tab drafts (like the inventory search keeps per-bond-type state) so each
  // tab can carry its own defaults / applied values independently.
  const [filterDataByType, setFilterDataByType] = useState(() => {
    const base = makeEmptyDraftsByType();
    if (initialApplied?.bondType && base[initialApplied.bondType]) {
      base[initialApplied.bondType] = { ...DEFAULT_FILTER_DATA, ...initialApplied };
    }
    return base;
  });
  const [tabValue, setTabValue] = useState(() =>
    initialApplied?.bondType ? getTabIndexForBondType(initialApplied.bondType) : 0
  );
  const [criteriaCount, setCriteriaCount] = useState(null);
  const [isCriteriaLoading, setIsCriteriaLoading] = useState(false);
  const [countError, setCountError] = useState(null);

  const activeBondType = SEC_MASTER_SEARCH_TABS[tabValue]?.bondType || BOND_TYPES.TREASURY;
  const filterData = filterDataByType[activeBondType] || DEFAULT_FILTER_DATA;

  // Tabs that should NOT receive default-prefill: those seeded from applied
  // filters or already touched by the user (so defaults never override them).
  const initializedTypesRef = useRef(
    new Set(initialApplied?.bondType ? [initialApplied.bondType] : [])
  );

  // Track which tabs have their filterData fully ready for the count query — either
  // seeded from nav-state applied filters, or with defaults already merged in.
  // The count effect gates on this so it never fires with a stale empty payload.
  const [defaultsMergedForTypes, setDefaultsMergedForTypes] = useState(
    () => new Set(initialApplied?.bondType ? [initialApplied.bondType] : [])
  );

  // Consume nav state once so a refresh doesn't re-apply stale criteria.
  const navStateConsumedRef = useRef(false);
  useEffect(() => {
    if (navStateConsumedRef.current) return;
    navStateConsumedRef.current = true;
    if (location.state?.secMasterFilterData || location.state?.bondType) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.pathname, navigate]);

  const setFilterField = useCallback(
    (name, value) => {
      // User-touched tab: lock it from a late-arriving default overwrite.
      initializedTypesRef.current.add(activeBondType);
      setFilterDataByType((prev) => ({
        ...prev,
        [activeBondType]: { ...prev[activeBondType], [name]: value },
      }));
    },
    [activeBondType]
  );

  // Ensure default ranges for the active tab are loaded (no-op if the SecMaster
  // screen already preloaded them, or if loaded for another tab).
  useEffect(() => {
    if (token) loadDefaults(token, activeBondType);
  }, [token, activeBondType, loadDefaults]);

  // Apply defaults to the active tab once available — but only if that tab was
  // not seeded from applied filters / user input (mirrors inventory's
  // `!isEdit && !preserveFilterData` guard).
  // Also mark the tab in defaultsMergedForTypes so the count effect can fire.
  useEffect(() => {
    const defaults = defaultsByType[activeBondType];
    if (!defaults) return;

    if (initializedTypesRef.current.has(activeBondType)) {
      // Already seeded from nav state or user input — just unblock the count
      // query if it hasn't been unblocked yet (e.g. defaults arrived late).
      setDefaultsMergedForTypes((prev) => {
        if (prev.has(activeBondType)) return prev;
        const next = new Set(prev);
        next.add(activeBondType);
        return next;
      });
      return;
    }

    initializedTypesRef.current.add(activeBondType);
    // Batch both updates so the count effect sees filterData and the "ready"
    // flag in the same render — payload will already be correct when it fires.
    setDefaultsMergedForTypes((prev) => {
      const next = new Set(prev);
      next.add(activeBondType);
      return next;
    });
    setFilterDataByType((prev) => ({
      ...prev,
      [activeBondType]: { ...prev[activeBondType], ...defaults },
    }));
  }, [defaultsByType, activeBondType]);

  // Adapt the SecMaster option lists into the shape renderFilterField expects.
  const filterOptions = useMemo(
    () => ({
      spRatingOptions: options.spRating || [],
      moodyRatingOptions: options.moodyRating || [],
      // Issuer autocomplete expects a flat string list, not { value, label }.
      issuerOptions: (options.issuer || []).map((o) =>
        typeof o === "string" ? o : o?.value ?? ""
      ),
      paymentFrequencyOptions: options.interestPaymentFrequency || [],
      sectorOptions: options.sector || [],
      conventionMethodOptions: options.conventionMethod || [],
      couponTypeOptions: options.couponType || [],
      currencyOptions: options.currency || [],
      govStateOptions: options.govState || [],
      incomeSourceOptions: options.incomeSource || [],
      issueCurrencyOptions: options.issueCurrency || [],
      securityChargeOptions: (options.securityCharge || []).map((o) =>
        typeof o === "string" ? o : o?.value ?? ""
      ),
      subTypeOptions: options.subType || [],
    }),
    [options]
  );

  // Backend payload for the active tab. Uses SecMaster-specific builder that
  // also maps identifier → searchString.
  const payload = useMemo(
    () => buildSecMasterFilterPayload({ ...filterData, bondType: activeBondType }),
    [filterData, activeBondType]
  );

  // Live "Key Search Criteria" count — debounced, re-runs as filters change.
  // Gated on defaultsMergedForTypes so it never fires with a stale empty payload
  // (i.e. before default ranges have been merged into filterData for this tab).
  useEffect(() => {
    if (!token) return undefined;
    if (!defaultsMergedForTypes.has(activeBondType)) {
      // Defaults not yet applied — keep the spinner and wait.
      setIsCriteriaLoading(true);
      setCriteriaCount(null);
      return undefined;
    }

    let cancelled = false;
    setIsCriteriaLoading(true);
    setCountError(null);

    const handle = setTimeout(async () => {
      try {
        const data = await fetchSecMasterItems(token, { page: 0, size: 1, ...payload });
        if (!cancelled) setCriteriaCount(data?.totalElements ?? 0);
      } catch (e) {
        if (!cancelled) {
          setCriteriaCount(0);
          setCountError(e?.message || "Failed to load matching securities count.");
        }
      } finally {
        if (!cancelled) setIsCriteriaLoading(false);
      }
    }, COUNT_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [token, payload, defaultsMergedForTypes, activeBondType]);

  const handleApply = useCallback(() => {
    navigate("/sec-master", {
      state: {
        secMasterFilters: payload,
        secMasterFilterData: { ...filterData, bondType: activeBondType },
        bondType: activeBondType,
        source: "sec-master-search",
      },
    });
  }, [navigate, payload, filterData, activeBondType]);

  // Clear resets the active tab back to its default ranges (matches the
  // inventory "Clear" behaviour, which restores defaults rather than blanking).
  const handleClear = useCallback(() => {
    const defaults = defaultsByType[activeBondType] || {};
    initializedTypesRef.current.add(activeBondType);
    setFilterDataByType((prev) => ({
      ...prev,
      [activeBondType]: { ...DEFAULT_FILTER_DATA, ...defaults },
    }));
  }, [activeBondType, defaultsByType]);

  const handleGoBack = useCallback(() => navigate(-1), [navigate]);

  return {
    tabValue,
    setTabValue,
    activeBondType,
    filterData,
    filterOptions,
    setFilterField,
    criteriaCount,
    isCriteriaLoading,
    countError,
    optionsError,
    handleApply,
    handleClear,
    handleGoBack,
  };
};
